import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { useAuthStore } from '../store/auth'
import type { FloorPlan } from '@shared/types'
import { CardGridSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Drawer } from '../components/ui/Drawer'
import { useProjectOptions } from '../hooks/useEntityOptions'
import { GlowCard } from '../components/ui/GlowCard'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const DELETE_ROLES = ['super_admin', 'admin', 'manager', 'technician']

interface FloorPlanListItem extends FloorPlan {
  room_count: number
  device_count: number
}

export default function FloorPlans() {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const navigate = useNavigate()
  const [plans, setPlans] = useState<FloorPlanListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState(projectSlug || '')
  const [formData, setFormData] = useState({ project_slug: projectSlug || '', name: '', floor_number: 1, canvas_width: 1200, canvas_height: 800 })
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<FloorPlanListItem | null>(null)
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null)
  const { options: projectOptions } = useProjectOptions()
  const { toast, toastError } = useToast()
  const userRole = useAuthStore(s => s.user?.role)
  const canDelete = DELETE_ROLES.includes(userRole || '')

  const loadPlans = useCallback(async () => {
    if (!selectedProject) {
      setPlans([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const result = await api.get<FloorPlanListItem[]>(`/floor-plans?project_slug=${selectedProject}`)
      setPlans(result)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [selectedProject])

  useEffect(() => { loadPlans() }, [loadPlans])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/floor-plans', formData)
      setDrawerOpen(false)
      setFormData({ project_slug: selectedProject, name: '', floor_number: 1, canvas_width: 1200, canvas_height: 800 })
      loadPlans()
    } catch { /* error */ }
    finally { setSaving(false) }
  }

  async function handleDelete(plan: FloorPlanListItem) {
    setDeletingSlug(plan.slug)
    try {
      await api.delete(`/floor-plans/${plan.slug}`)
      setPlans(prev => prev.filter(p => p.id !== plan.id))
      toast('Floor plan deleted')
    } catch (err) {
      toastError('Failed to delete floor plan', err)
    } finally {
      setDeletingSlug(null)
      setConfirmDelete(null)
    }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Floor Plans</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Floor Plan</Button>
      </div>

      {!projectSlug && (
        <div className="mb-4">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="input max-w-xs"
            style={{ fontSize: 13 }}
          >
            <option value="">Select project...</option>
            {projectOptions.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      )}

      {!selectedProject ? (
        <EmptyState
          title="Select a project"
          description="Choose a project to view its floor plans."
        />
      ) : loading ? (
        <CardGridSkeleton cards={3} />
      ) : plans.length === 0 ? (
        <EmptyState
          title="No floor plans"
          description="Create a floor plan to start designing device layouts."
          action={<Button onClick={() => setDrawerOpen(true)}>New Floor Plan</Button>}
        />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: `calc(16px * var(--density))`,
        }}>
          {plans.map((plan, idx) => (
            <GlowCard
              key={plan.id}
              onClick={() => navigate(`/floor-plans/${plan.slug}`)}
              style={{ padding: 16 }}
              delay={idx * 50}
            >
              {/* Thumbnail placeholder */}
              <div style={{
                height: 140, borderRadius: 'var(--r-m)', marginBottom: 12,
                background: 'var(--bg-2)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-3)' }}>
                  <path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z" />
                  <path d="M8 2v16" />
                  <path d="M16 6v16" />
                </svg>
              </div>

              {/* Plan info */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>{plan.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, display: 'flex', gap: 12 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6z" />
                      </svg>
                      {plan.room_count ?? 0} rooms
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 2v4m0 12v4m-8-10h4m12 0h4" />
                      </svg>
                      {plan.device_count ?? 0} devices
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                    {plan.canvas_width} × {plan.canvas_height}px · Floor {plan.floor_number}
                  </div>
                </div>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={e => { e.stopPropagation(); setConfirmDelete(plan) }}
                    title="Delete floor plan"
                    style={{ color: 'var(--text-3)', flexShrink: 0 }}
                    icon={<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                    </svg>}
                  />
                )}
              </div>
            </GlowCard>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div
          onClick={() => setConfirmDelete(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'in-fade 150ms var(--ease)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-1)', borderRadius: 'var(--r-xl)',
              border: '1px solid var(--border-h)', boxShadow: 'var(--sh-lg)',
              padding: 24, width: 400,
              animation: 'in-scale 200ms var(--ease-sp)',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>Delete Floor Plan</h3>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
              Delete &ldquo;{confirmDelete.name}&rdquo;? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={!!deletingSlug}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => handleDelete(confirmDelete)}
                disabled={!!deletingSlug}
                loading={deletingSlug === confirmDelete.slug}
              >
                {deletingSlug === confirmDelete.slug ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Floor Plan">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Project *</label>
            <select
              value={formData.project_slug}
              onChange={(e) => {
                setFormData({ ...formData, project_slug: e.target.value })
                setSelectedProject(e.target.value)
              }}
              className="input"
              required
            >
              <option value="">Select project...</option>
              {projectOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Name *</label>
            <input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
              placeholder="e.g. Ground Floor, Building A"
            />
          </div>
          <div>
            <label className="label">Floor Number</label>
            <input
              type="number"
              value={formData.floor_number}
              onChange={(e) => setFormData({ ...formData, floor_number: parseInt(e.target.value) || 1 })}
              className="input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Canvas Width (px)</label>
              <input
                type="number"
                value={formData.canvas_width}
                onChange={(e) => setFormData({ ...formData, canvas_width: parseInt(e.target.value) || 1200 })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Canvas Height (px)</label>
              <input
                type="number"
                value={formData.canvas_height}
                onChange={(e) => setFormData({ ...formData, canvas_height: parseInt(e.target.value) || 800 })}
                className="input"
              />
            </div>
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%' }}>
            Create Floor Plan
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
